export const PLAN_PERIODS = 12;
export const MAX_PLAN_MARKETING_MONTHS = 36;
export const DEFAULT_FUNDING_PCT = 80;
export const DEFAULT_REV_SHARE_PCT = 80;
export const DEFAULT_PLAN_MARKETING_MONTHS = 12;
export const MARKETING_PLAN_MONTHS = DEFAULT_PLAN_MARKETING_MONTHS + 1;
export const START_YEAR = 2026;
export const START_MONTH = 5;
export const MAX_FACILITY_FEE_INSTALLMENTS = 12;
export const DEFAULT_MAX_EXPOSURE = 1000000;
export const FACILITY_SIZE_TOOLTIP =
  'Defaults to max exposure. Changing max exposure fills this in. You can override it. The facility fee uses this amount.';

export const DEFAULT_ROAS = [
  5, 14, 28, 46, 62, 75, 85, 93, 100, 106, 111, 115,
];

/** Default bull / bear scenario config (disabled, relative % mode). */
export function createDefaultScenarios() {
  return {
    bull: { enabled: false, mode: 'percent', value: 0 },
    bear: { enabled: false, mode: 'percent', value: 0 },
  };
}

export function createDefaultAssistSession() {
  return {
    phase: 'menu',
    menuTrack: 'root',
    menuLevel: 'root',
  };
}

export function createEmptyModel() {
  return {
    modelName: '',
    planStartYear: START_YEAR,
    planStartMonth: START_MONTH,
    firstPlanMonthIsCurrentMonth: false,
    roas: [0],
    roasSource: 'Manual entry',
    roasMode: 'manual',
    gpm: 100,
    gpmSource: 'Default',
    revenueGrowthYoy: 0,
    revenueGrowthYoySource: 'Default',
    opex: 0,
    opexSource: 'Manual entry',
    debt: 0,
    debtSource: 'Manual entry',
    totalAssets: 0,
    totalAssetsSource: 'Manual entry',
    totalLiabilities: 0,
    totalLiabilitiesSource: 'Manual entry',
    safe: 0,
    safeSource: 'Manual entry',
    beginCash: 0,
    beginCashSource: 'Manual entry',
    netBurn: 0,
    netBurnSource: 'Manual entry',
    runway: null,
    runwaySource: 'Default',
    predictedRunoff: 0,
    predictedRunoffSource: 'Default',
    cacPredictionRatio: null,
    cacPredictionRatioSource: 'Default',
    liquidValue: null,
    liquidValueSource: 'Default',
    cashBalance: null,
    cashBalanceSource: 'Default',
    balanceSheet: null,
    balanceSheetSource: 'Default',
    lastMarketingSpend: 0,
    lastMarketingSpendSource: 'Default',
    maxMarketingRoas100: 0,
    maxMarketingRoas100Source: 'Default',
    uwKpiNotes: '',
    investorScore: null,
    managementScore: null,
    stressTestingScore: null,
    commitmentMonths: null,
    defaultFundingPct: DEFAULT_FUNDING_PCT,
    defaultRevSharePct: DEFAULT_REV_SHARE_PCT,
    marketing: Array(MARKETING_PLAN_MONTHS).fill(0),
    fundingPct: Array(MARKETING_PLAN_MONTHS).fill(DEFAULT_FUNDING_PCT),
    revSharePct: Array(MARKETING_PLAN_MONTHS).fill(DEFAULT_REV_SHARE_PCT),
    marketingPlanSource: 'Manual entry',
    facilitySize: 0,
    facilityFeePct: 0,
    facilityFeeRepaymentMonth: 0,
    facilityFeeFirstPaymentMonth: null,
    facilityFeeInstallments: 1,
    feeType: 'Annual XIRR',
    feePct: 17,
    gracePeriod: 1,
    marketingGracePeriod: 1,
    revenueShareForRepaymentPct: null,
    dealIrrLearningFactor: 0.01,
    dealIrrHillK: null,
    monthlyIrrPct: [],
    numberOfRepayments: null,
    runoffExposureCohortCount: null,
    maxExposure: DEFAULT_MAX_EXPOSURE,
    maxMonthlyDraw: null,
    maxDrawIncreasePct: null,
    planMaxExposure: Array(MARKETING_PLAN_MONTHS).fill(null),
    planMaxMonthlyDraw: Array(MARKETING_PLAN_MONTHS).fill(null),
    marketingPlanManualMode: false,
    autoFixFundingEnabled: false,
    planDrawAmount: Array(MARKETING_PLAN_MONTHS).fill(null),
    futureFundingRoasSource: 'hill',
    hillRoasEfficiencyRatio: 1,
    cohortSheet: null,
    cohortSheetExpanded: false,
    assistSession: createDefaultAssistSession(),
    accountingSnapshot: null,
    scenarios: createDefaultScenarios(),
  };
}

/** Demo preset used by tests and "Reset to AI" ROAS button. */
export const DEFAULTS = {
  modelName: '',
  planStartYear: START_YEAR,
  planStartMonth: START_MONTH,
  firstPlanMonthIsCurrentMonth: false,
  roas: [...DEFAULT_ROAS],
  roasSource: 'Default preset',
  roasMode: 'ai',
  gpm: 100,
  gpmSource: 'Default',
  revenueGrowthYoy: 0,
  revenueGrowthYoySource: 'Default',
  opex: 80000,
  opexSource: 'Default',
  debt: 0,
  debtSource: 'Default',
  totalAssets: 0,
  totalAssetsSource: 'Default',
  totalLiabilities: 0,
  totalLiabilitiesSource: 'Default',
  safe: 0,
  safeSource: 'Default',
  beginCash: 1040000,
  beginCashSource: 'Default',
  netBurn: 0,
  netBurnSource: 'Default',
  runway: null,
  runwaySource: 'Default',
  predictedRunoff: 0,
  predictedRunoffSource: 'Default',
  cacPredictionRatio: null,
  cacPredictionRatioSource: 'Default',
  liquidValue: null,
  liquidValueSource: 'Default',
  cashBalance: null,
  cashBalanceSource: 'Default',
  balanceSheet: null,
  balanceSheetSource: 'Default',
  lastMarketingSpend: 0,
  lastMarketingSpendSource: 'Default',
  maxMarketingRoas100: 0,
  maxMarketingRoas100Source: 'Default',
  uwKpiNotes: '',
  marketing: [
    0,
    400000, 450000, 500000, 550000, 600000, 750000, 900000, 650000, 600000,
    650000, 700000, 750000,
  ],
  fundingPct: [0, ...Array(PLAN_PERIODS).fill(50)],
  revSharePct: [50, ...Array(PLAN_PERIODS).fill(50)],
  marketingPlanSource: 'Default',
  facilitySize: 5000000,
  facilityFeePct: 2,
  facilityFeeRepaymentMonth: 0,
  facilityFeeInstallments: 1,
  feeType: 'Annual XIRR',
  feePct: 17,
  gracePeriod: 1,
  marketingGracePeriod: 1,
  numberOfRepayments: null,
  maxExposure: DEFAULT_MAX_EXPOSURE,
  maxMonthlyDraw: null,
  maxDrawIncreasePct: null,
  futureFundingRoasSource: 'hill',
  hillRoasEfficiencyRatio: 1,
  cohortSheet: null,
  cohortSheetExpanded: false,
};
